import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { NeonMultiSelectField, type MultiSelectOption } from '@ps-refarch-ux/neon';
import { useDebouncedValue } from '@/hooks/use-debounce';
import {
  useInfiniteCourseCatalogPicker,
  usePrefetchRelationshipCatalogBrowseWhenEnabled,
} from '@/repositories/course-repository';
import type { Course } from '@/types';
import { toNeonSafeCourseOptionDomId } from './neon-safe-course-option-dom-id';
import './add-course-catalog-multi-select-dropdown.module.scss';
import styles from './add-course-page.module.scss';

export interface CourseCatalogMultiSelectItem {
  id: string;
  name: string;
}

/** Matches course catalog top search debounce (`useCourseCatalogFilters` → `useDebouncedValue(..., 300)`). */
const PICKER_FILTER_DEBOUNCE_MS = 300;
const SCROLL_LOAD_THRESHOLD_PX = 120;
const PICKER_POLL_MS = 350;

/** Neon multi-select uses a drawer when option count >= threshold (default 100). Keep popper/dropdown for full catalog. */
const COURSE_CATALOG_MULTI_SELECT_MODAL_THRESHOLD = 10_000;

function isNeonPickerFilterInputEl(el: HTMLInputElement): boolean {
  return (
    el.classList.contains('neon-item-picker-filter-input') ||
    (typeof el.id === 'string' && el.id.includes('searchable-select-input-field'))
  );
}

/**
 * Staff/student embed mounts React under `#cp-root` with an open shadow root (`main-embed.tsx`).
 * `document.querySelector` does not pierce that shadow root, so we never saw the expanded
 * `neon-multi-select-field` and filter typing never updated `pickerFilterRaw` — no debounced
 * POST to `getCoursesModern` with `search`.
 */
function getCpRootShadowRoot(): ShadowRoot | null {
  return document.getElementById('cp-root')?.shadowRoot ?? null;
}

function queryOpenNeonMultiSelectField(fieldId: string): HTMLElement | null {
  const selector = `neon-multi-select-field[aria-expanded="true"]#${CSS.escape(fieldId)}`;
  return (
    document.querySelector<HTMLElement>(selector) ??
    getCpRootShadowRoot()?.querySelector<HTMLElement>(selector) ??
    null
  );
}

type NeonChip = {
  id: string;
  dataText: string;
  dataTooltip: string;
  dataSubtext?: string;
};

type NeonChipSetElement = HTMLElement & {
  chips?: NeonChip[];
};

function getNeonMultiSelectField(fieldId: string): HTMLElement | null {
  return (
    document.getElementById(fieldId) ??
    getCpRootShadowRoot()?.getElementById(fieldId) ??
    getCpRootShadowRoot()?.querySelector<HTMLElement>(`#${CSS.escape(fieldId)}`) ??
    null
  );
}

/**
 * Neon item-picker may host the scrollable list inside a shadow root; `querySelector` on the
 * host alone returns null, so infinite-scroll never attaches and only the first API page shows.
 */
function findPickerScrollable(pickerRoot: Element | null): HTMLElement | null {
  if (!pickerRoot) {
    return null;
  }
  const fromHost = pickerRoot.querySelector<HTMLElement>('[class*="layout-scrollable"]');
  if (fromHost) {
    return fromHost;
  }
  const root = (pickerRoot as HTMLElement).shadowRoot;
  if (root) {
    const fromShadow = root.querySelector<HTMLElement>('[class*="layout-scrollable"]');
    if (fromShadow) {
      return fromShadow;
    }
  }
  for (const el of pickerRoot.querySelectorAll('*')) {
    const sr = (el as HTMLElement).shadowRoot;
    if (sr) {
      const hit = sr.querySelector<HTMLElement>('[class*="layout-scrollable"]');
      if (hit) {
        return hit;
      }
    }
  }
  return null;
}

export interface AddCourseCatalogMultiSelectProps {
  id: string;
  fieldLabelText: string;
  helperText?: string;
  /** Defaults to `${id}-helper` when helperText is set */
  helperId?: string;
  placeholderText: string;
  selected: CourseCatalogMultiSelectItem[];
  onSelectedChange: (next: CourseCatalogMultiSelectItem[]) => void;
  /** Unique prefix for Neon option `id` values (avoid collisions across fields) */
  optionIdPrefix: string;
  /** When false, catalog query is disabled (e.g. gated feature section unmounted) */
  enabledFetch?: boolean;
  /** When true, skip the visible label and helper; `fieldLabelText` still used for `dataLabelText` (a11y). */
  hideOuterCaption?: boolean;
}

export function AddCourseCatalogMultiSelect({
  id,
  fieldLabelText,
  helperText,
  helperId: helperIdProp,
  placeholderText,
  selected,
  onSelectedChange,
  optionIdPrefix,
  enabledFetch = true,
  hideOuterCaption = false,
}: AddCourseCatalogMultiSelectProps): ReactElement {
  const helperId = helperIdProp ?? `${id}-helper`;
  const showHelper =
    !hideOuterCaption && helperText !== undefined && helperText !== null && helperText !== '';

  const [pickerFilterRaw, setPickerFilterRaw] = useState('');
  const debouncedPickerFilter = useDebouncedValue(pickerFilterRaw, PICKER_FILTER_DEBOUNCE_MS);

  usePrefetchRelationshipCatalogBrowseWhenEnabled(enabledFetch);

  const {
    data: infiniteData,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteCourseCatalogPicker(debouncedPickerFilter, { enabled: enabledFetch });

  // While the user has typed a filter that hasn't resolved yet — either still within the debounce
  // window (raw !== debounced) or the debounced query is in flight — the picker must show a loading
  // state. Otherwise Neon renders "No results for <text>" against stale/empty options before the
  // matching courses arrive.
  const trimmedRawFilter = pickerFilterRaw.trim();
  const isSearchPending =
    enabledFetch &&
    trimmedRawFilter.length > 0 &&
    (trimmedRawFilter !== debouncedPickerFilter.trim() || isFetching);

  const catalogBusy = isLoading || isFetchingNextPage || isSearchPending;
  const catalogBusyRef = useRef(catalogBusy);
  catalogBusyRef.current = catalogBusy;

  const fetchNextRef = useRef(fetchNextPage);
  fetchNextRef.current = fetchNextPage;
  const hasNextRef = useRef(!!hasNextPage);
  hasNextRef.current = !!hasNextPage;
  const isFetchingNextRef = useRef(isFetchingNextPage);
  isFetchingNextRef.current = isFetchingNextPage;

  // The user's intended filter text, updated SYNCHRONOUSLY on every real input event (typing AND
  // Backspace). React state refs only update on re-render, so they are momentarily stale right after
  // a keystroke — the requestAnimationFrame guard below compares against this ref so a real edit is
  // never confused with a Neon reset (Neon resets the uncontrolled filter input to a stale value).
  const userFilterRef = useRef('');

  const catalogRows = useMemo((): Course[] => {
    const seen = new Set<string>();
    const rows: Course[] = [];
    for (const page of infiniteData?.pages ?? []) {
      for (const c of page.data) {
        if (!seen.has(c.id)) {
          seen.add(c.id);
          rows.push(c);
        }
      }
    }
    return rows;
  }, [infiniteData?.pages]);

  const catalogRowsRef = useRef(catalogRows);
  catalogRowsRef.current = catalogRows;

  const options = useMemo((): MultiSelectOption[] => {
    const normalizedFilter = pickerFilterRaw.trim().toLowerCase();
    const shouldIncludeIdInLabelForSearch = normalizedFilter.length > 0;

    const byId = new Map<string, MultiSelectOption>();
    for (const c of catalogRows) {
      const labelText = shouldIncludeIdInLabelForSearch ? `${c.name} ${c.id}` : c.name;
      byId.set(c.id, {
        id: toNeonSafeCourseOptionDomId(optionIdPrefix, c.id),
        name: c.id,
        labelText,
        helperText: c.id,
      });
    }
    for (const sel of selected) {
      if (!byId.has(sel.id)) {
        const labelText = shouldIncludeIdInLabelForSearch ? `${sel.name} ${sel.id}` : sel.name;
        byId.set(sel.id, {
          id: toNeonSafeCourseOptionDomId(optionIdPrefix, sel.id),
          name: sel.id,
          labelText,
          helperText: sel.id,
        });
      }
    }
    return Array.from(byId.values());
  }, [catalogRows, selected, optionIdPrefix, pickerFilterRaw]);

  const modelValue = useMemo(() => selected.map((c) => c.id), [selected]);

  useEffect(() => {
    const chipContentById = new Map(
      selected.map((course) => [
        toNeonSafeCourseOptionDomId(optionIdPrefix, course.id),
        {
          text: course.name,
          subtext: course.id,
          tooltip: `${course.name} | ${course.id}`,
        },
      ])
    );
    const animationFrame = requestAnimationFrame(() => {
      const field = getNeonMultiSelectField(id);
      const chipSetId = `#neon-multi-select-chipSet-${CSS.escape(id)}`;
      const chipSet =
        field?.querySelector<NeonChipSetElement>(chipSetId) ??
        field?.shadowRoot?.querySelector<NeonChipSetElement>(chipSetId);
      if (!chipSet?.chips) {
        return;
      }
      chipSet.chips = chipSet.chips.map((chip) => {
        const chipContent = chipContentById.get(chip.id);
        return chipContent
          ? {
              ...chip,
              dataText: chipContent.text,
              dataSubtext: chipContent.subtext,
              dataTooltip: chipContent.tooltip,
            }
          : chip;
      });
    });
    return () => cancelAnimationFrame(animationFrame);
  }, [id, optionIdPrefix, options, selected]);

  const onModelValueChange = useCallback(
    (ids: string[] | undefined) => {
      const nextIds = ids ?? [];
      const catalogSnapshot = catalogRowsRef.current;
      const prevSelectedSnapshot = selected;
      const nameById = new Map<string, string>();
      for (const row of catalogSnapshot) {
        nameById.set(row.id, row.name);
      }
      for (const item of prevSelectedSnapshot) {
        nameById.set(item.id, item.name);
      }
      onSelectedChange(
        nextIds.map((courseId) => ({
          id: courseId,
          name: nameById.get(courseId) ?? courseId,
        }))
      );
    },
    [onSelectedChange, selected]
  );

  const handlePopperClose = useCallback(() => {
    userFilterRef.current = '';
    setPickerFilterRaw('');
  }, []);

  /**
   * Neon opens the option list in a popper (often under document / react root, not under our wrapper).
   * Scope filter input to this field via class/id — does NOT require aria-expanded on the host element
   * (Neon may set it on an inner element, breaking the old openHost guard).
   */
  useEffect(() => {
    const onInputCapture = (e: Event) => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement)) {
        return;
      }
      const pickerRootId = `${id}-item-picker`;
      const isOwnPickerInputById =
        typeof t.id === 'string' &&
        (t.id === `${pickerRootId}-searchable-select-input-field` ||
          t.id.startsWith(`${pickerRootId}-`));

      // Fallback: verify the event originated from this specific picker instance
      const picker = document.getElementById(pickerRootId);
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      const inOurPicker = picker !== null && (path.includes(picker) || picker.contains(t));
      if (!isOwnPickerInputById && !inOurPicker) {
        return;
      }

      if (isNeonPickerFilterInputEl(t) || t.type === 'text' || t.type === 'search') {
        userFilterRef.current = t.value;
        setPickerFilterRaw(t.value);
      }
    };
    document.addEventListener('input', onInputCapture, true);
    return () => document.removeEventListener('input', onInputCapture, true);
  }, [id]);

  /** Infinite scroll on the item-picker list (element id `${id}-item-picker` per Neon). */
  useEffect(() => {
    let attachedEl: HTMLElement | null = null;

    const onScroll = () => {
      if (!attachedEl || isFetchingNextRef.current || !hasNextRef.current || !enabledFetch) {
        return;
      }
      const { scrollTop, clientHeight, scrollHeight } = attachedEl;
      if (scrollHeight - scrollTop - clientHeight < SCROLL_LOAD_THRESHOLD_PX) {
        void fetchNextRef.current();
      }
    };

    const tick = () => {
      const openHost = queryOpenNeonMultiSelectField(id);
      if (!openHost) {
        if (attachedEl) {
          attachedEl.removeEventListener('scroll', onScroll);
          attachedEl = null;
        }
        return;
      }
      /** Neon item-picker defaults filter placeholder to i18n "Search"; override to match mandated-courses NeonSearch copy. Search icon stays from `.neon-item-picker-filter-input-icon`. */
      const filterInput = document.getElementById(
        `${id}-item-picker-searchable-select-input-field`
      );
      if (filterInput instanceof HTMLInputElement) {
        if (filterInput.placeholder !== placeholderText) {
          filterInput.placeholder = placeholderText;
        }
        if (filterInput.getAttribute('aria-label') !== placeholderText) {
          filterInput.setAttribute('aria-label', placeholderText);
        }
        // Note: filter value is kept in sync by the requestAnimationFrame guard below
        // (`onInputCapture` updates React state; the guard restores the DOM value when Neon
        // silently wipes it on option refresh). The poll intentionally does NOT touch the value.
      }
      const pickerRoot = document.getElementById(`${id}-item-picker`);
      // Reflect the current busy state onto the portaled picker so CSS can suppress Neon's
      // premature "No results" empty-state while the debounced search is still resolving.
      if (pickerRoot) {
        if (catalogBusyRef.current) {
          pickerRoot.setAttribute('data-cp-search-loading', 'true');
        } else {
          pickerRoot.removeAttribute('data-cp-search-loading');
        }
      }
      const scrollable = findPickerScrollable(pickerRoot);
      if (scrollable && scrollable !== attachedEl) {
        if (attachedEl) {
          attachedEl.removeEventListener('scroll', onScroll);
        }
        attachedEl = scrollable;
        attachedEl.addEventListener('scroll', onScroll, { passive: true });
      }
    };

    const poll = window.setInterval(tick, PICKER_POLL_MS);
    return () => {
      window.clearInterval(poll);
      if (attachedEl) {
        attachedEl.removeEventListener('scroll', onScroll);
      }
    };
  }, [id, enabledFetch, placeholderText]);

  // Toggle the loading marker immediately when busy state changes (the poll above only runs every
  // PICKER_POLL_MS, which would let the "No results" flash appear for a moment). The portaled picker
  // element may not exist until Neon opens it; the poll re-applies the attribute in that case.
  useEffect(() => {
    const pickerRoot = document.getElementById(`${id}-item-picker`);
    if (!pickerRoot) {
      return;
    }
    if (catalogBusy) {
      pickerRoot.setAttribute('data-cp-search-loading', 'true');
    } else {
      pickerRoot.removeAttribute('data-cp-search-loading');
    }
  }, [catalogBusy, id]);

  /**
   * Neon manages the popper filter input as an uncontrolled field and silently resets it whenever
   * the `options` prop changes — which happens every time a debounced search response arrives or the
   * loading state toggles. Crucially, Neon resets it to a STALE value (often the text from before the
   * last edit), not just to empty. At human typing speed this landed between keystrokes and reverted
   * the last edit (e.g. deleting "biol" -> "bio" snapped back to "biol").
   *
   * `userFilterRef` holds the user's intended text and is updated SYNCHRONOUSLY from every real input
   * event (typing AND Backspace). Therefore any time the DOM value differs from `userFilterRef`, the
   * change did NOT come from the user (a real edit would have updated the ref first) — it is a Neon
   * reset, so we restore the intended text. We must NOT use a length comparison here: Neon's stale
   * value can be longer than the intended value, so "adopt if longer" would re-introduce the bug.
   *
   * We deliberately do NOT dispatch an input event on restore: re-entering Neon's own input handler
   * causes an oscillation. On the next real keystroke Neon reads the (restored) DOM value and heals.
   */
  useEffect(() => {
    let rafHandle = 0;
    let closedPollHandle = 0;
    // Guard so the self-rescheduling loop stops immediately on cleanup and never queues another
    // frame/timeout after unmount. Without this the loop reschedules forever — in tests this
    // accumulates thousands of pending frames across the suite and exhausts the heap.
    let running = true;

    const restoreFilterValue = () => {
      if (!running) {
        return;
      }
      const filterInput = document.getElementById(
        `${id}-item-picker-searchable-select-input-field`
      );
      if (!(filterInput instanceof HTMLInputElement)) {
        // Popper is closed — Neon removes the portaled filter input from the DOM. Avoid scheduling an
        // animation frame ~60x/sec while the picker is dormant (relevant if many pickers are mounted
        // on one page); fall back to a slow poll and resume rAF cadence as soon as it reopens.
        closedPollHandle = window.setTimeout(restoreFilterValue, PICKER_POLL_MS);
        return;
      }
      const domValue = filterInput.value ?? '';
      const desiredValue = userFilterRef.current;
      if (domValue !== desiredValue) {
        filterInput.value = desiredValue;
        const caret = desiredValue.length;
        try {
          filterInput.setSelectionRange(caret, caret);
        } catch {
          // setSelectionRange can throw for unsupported input types; safe to ignore.
        }
      }
      rafHandle = requestAnimationFrame(restoreFilterValue);
    };

    rafHandle = requestAnimationFrame(restoreFilterValue);
    return () => {
      running = false;
      cancelAnimationFrame(rafHandle);
      window.clearTimeout(closedPollHandle);
    };
  }, [id]);

  return (
    <div className={styles.addCourseFieldGroup}>
      {!hideOuterCaption ? (
        <label className={styles.addCourseFieldLabel} htmlFor={id}>
          {fieldLabelText}
        </label>
      ) : null}
      {showHelper ? (
        <p id={helperId} className={styles.addCourseHelper}>
          {helperText}
        </p>
      ) : null}
      <NeonMultiSelectField
        id={id}
        dataLabelText={fieldLabelText}
        dataShowLabel={false}
        dataPopperPlacement="top-end"
        {...(showHelper ? { 'aria-describedby': helperId } : {})}
        dataDropDownText={placeholderText}
        dataPlaceholderText={placeholderText}
        dataShowFilter
        dataShowSelectedItemsList
        dataChipSetEnableClearAll="true"
        dataModalThreshold={COURSE_CATALOG_MULTI_SELECT_MODAL_THRESHOLD}
        dataIsLoading={catalogBusy ? 'true' : undefined}
        neonPopperClose={handlePopperClose}
        options={options}
        modelValue={modelValue}
        modelValueChange={onModelValueChange}
      />
    </div>
  );
}
